import { css } from "@emotion/css";
import { Callout, InlineMessage, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { ArrowRotateIcon } from "@octopusdeploy/design-system-icons";
import { fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionProperties, ProcessTemplateVersionSummary } from "@octopusdeploy/octopus-server-client";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import * as React from "react";
import semver from "semver/preload";
import type { ProcessTemplateConsumerActionDispatcher } from "~/analytics/Analytics";
import { ProcessTemplateConsumerAction } from "~/analytics/Analytics";
import type { ProcessTemplateDetails } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { getProcessTemplateVersionMaskFromActionProperties } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
import { ExpandableFormSection, FormSection, FormSectionHeading, Summary } from "~/components/form/index";
import { ProcessTemplateUpdateDialog } from "./ProcessTemplateUpdateDialog";
interface ProcessTemplateVersioningSectionProps {
    initialActionProperties: ActionProperties;
    initialProcessTemplateVersion: string | undefined | null;
    setVersionMaskProperty: (versionMask: string) => void;
    selectedProcessTemplateDetails: ProcessTemplateDetails | undefined;
    latestProcessTemplateVersionSummary: ProcessTemplateVersionSummary;
    selectedUsageConstraint: UsageConstraint;
    setSelectedUsageConstraint: (value: UsageConstraint) => void;
    selectedUpdateBehaviour: UpdateBehaviour;
    setSelectedUpdateBehaviour: (value: UpdateBehaviour) => void;
    processTemplateConsumerActionDispatch: ProcessTemplateConsumerActionDispatcher;
    processTemplateSlug: string;
    onUpdateToLatestVersion: () => void;
    currentVersion: string | undefined;
    templateName: string;
}
export function ProcessTemplateVersioningSection({ initialActionProperties, initialProcessTemplateVersion, setVersionMaskProperty, latestProcessTemplateVersionSummary, selectedUsageConstraint, setSelectedUsageConstraint, selectedUpdateBehaviour, setSelectedUpdateBehaviour, processTemplateConsumerActionDispatch, processTemplateSlug, onUpdateToLatestVersion, currentVersion, templateName, }: ProcessTemplateVersioningSectionProps) {
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = React.useState(false);
    const initialVersionMask = getProcessTemplateVersionMaskFromActionProperties(initialActionProperties);
    const initialUpdateBehavior = mapMaskToUpdateBehaviour(initialVersionMask);
    const isFeatureEnabled = useIsLicensedFeatureEnabled("ProcessTemplates");
    const isUpdateAvailable = currentVersion && latestProcessTemplateVersionSummary.LatestStableVersion ? semver.ltr(currentVersion, latestProcessTemplateVersionSummary.LatestStableVersion) : false;
    const onUpdateBehaviourChanged = (value: UpdateBehaviour) => {
        if (!latestProcessTemplateVersionSummary.LatestStableVersion) {
            throw Error("Cannot change update behaviour when no stable version is available.");
        }
        setSelectedUpdateBehaviour(value);
        const versionMask = getUpdatedVersionMask(latestProcessTemplateVersionSummary.LatestStableVersion, initialProcessTemplateVersion, value, initialUpdateBehavior);
        setVersionMaskProperty(versionMask);
        processTemplateConsumerActionDispatch("Process Template Version Preferences Set", {
            type: "Process Template Version Preferences Set",
            action: ProcessTemplateConsumerAction.SelectVersionPreference,
            processTemplateId: processTemplateSlug,
            versioningPreference: value,
            isPreRelease: false,
        });
    };
    const onUsageConstraintChanged = (value: UsageConstraint) => {
        if (value === UsageConstraint.PreRelease) {
            setVersionMaskProperty(UpdateBehaviour.PreRelease);
            setSelectedUsageConstraint(UsageConstraint.PreRelease);
            processTemplateConsumerActionDispatch("Process Template Version Preferences Set", {
                type: "Process Template Version Preferences Set",
                action: ProcessTemplateConsumerAction.SelectVersionPreference,
                processTemplateId: processTemplateSlug,
                versioningPreference: UpdateBehaviour.PreRelease,
                isPreRelease: true,
            });
        }
        else {
            setSelectedUsageConstraint(value);
            onUpdateBehaviourChanged(UpdateBehaviour.Minor);
        }
    };
    const getPreferencesSummary = () => {
        if (selectedUsageConstraint === UsageConstraint.PreRelease) {
            return Summary.summary("Template usage will use the latest pre-release version");
        }
        if (selectedUpdateBehaviour === UpdateBehaviour.Minor) {
            return Summary.summary("Template usage will automatically update whenever a patch or minor version is published.");
        }
        if (selectedUpdateBehaviour === UpdateBehaviour.Patch) {
            return Summary.summary("Template usage will automatically update whenever a patch version is published");
        }
        return Summary.placeholder("No preference selected");
    };
    const handleOpenUpdateDialog = () => {
        setIsUpdateDialogOpen(true);
    };
    const handleCloseUpdateDialog = () => {
        setIsUpdateDialogOpen(false);
    };
    return (<>
            <FormSectionHeading title="Versioning"/>
            {latestProcessTemplateVersionSummary.IsOnlyPreRelease ? <OnlyPreReleaseCallout /> : null}

            <FormSection title={isUpdateAvailable && isFeatureEnabled ? (<span className={styles.templateVersionTitle}>
                            Template version
                            <ArrowRotateIcon color={themeTokens.color.icon.info} size={20}/>
                        </span>) : ("Template version")} help={<>{currentVersion && <span>Currently using: v{currentVersion}</span>}</>}>
                {isUpdateAvailable && isFeatureEnabled && (<>
                        <InlineMessage type="information" title="Version update:" description={`v${latestProcessTemplateVersionSummary.LatestStableVersion} of ${templateName} is available.`} action={{
                label: "Review and update",
                onClick: handleOpenUpdateDialog,
            }}/>
                        <ProcessTemplateUpdateDialog open={isUpdateDialogOpen} onClose={handleCloseUpdateDialog} onUpdateVersion={onUpdateToLatestVersion} currentVersion={currentVersion} latestVersion={latestProcessTemplateVersionSummary.LatestStableVersion}/>
                    </>)}
            </FormSection>
            <ExpandableFormSection title="Versioning preferences" summary={getPreferencesSummary()} help={<span>Choose how you want to receive updates for this template.</span>} errorKey="Versioning preferences" isExpandedByDefault={latestProcessTemplateVersionSummary.IsOnlyPreRelease}>
                {latestProcessTemplateVersionSummary.LatestPreReleaseVersion && (<>
                        <RadioButtonGroup value={selectedUsageConstraint} onChange={onUsageConstraintChanged} title="Select usage constraint" accessibleName="Usage constraint">
                            <RadioButton label="Stable release" value={UsageConstraint.Stable} disabled={latestProcessTemplateVersionSummary.IsOnlyPreRelease}/>
                            <RadioButton label="Pre-release" value={UsageConstraint.PreRelease}/>
                            <span className={styles.helperText}>
                                Current pre-release version: {latestProcessTemplateVersionSummary.LatestPreReleaseVersion}. Pre-releases are intended for testing purposes, not regular deployments. Proceed with caution.
                            </span>
                        </RadioButtonGroup>
                    </>)}
                {selectedUsageConstraint === UsageConstraint.Stable && !latestProcessTemplateVersionSummary.IsOnlyPreRelease && (<RadioButtonGroup value={selectedUpdateBehaviour} onChange={onUpdateBehaviourChanged} title="Select update behaviour" accessibleName="Update behaviour">
                        <RadioButton label="Accept minor changes" value={UpdateBehaviour.Minor}/>
                        <span className={styles.helperText}>Automatically update the template whenever a patch or minor version is published.</span>
                        <RadioButton label="Accept patches" value={UpdateBehaviour.Patch}/>
                        <span className={styles.helperText}>Automatically update the template whenever a patch is published.</span>
                    </RadioButtonGroup>)}
            </ExpandableFormSection>
        </>);
}
export enum UsageConstraint {
    Stable = "Stable",
    PreRelease = "PreRelease"
}
export enum UpdateBehaviour {
    Minor = "Minor",// Includes Minor and Patch updates
    Patch = "Patch",// Patch updates only
    PreRelease = "PreRelease",
    Invalid = "Invalid"
}
export function mapMaskToUpdateBehaviour(mask: string): UpdateBehaviour {
    switch (true) {
        case mask.toLowerCase() === "prerelease":
            return UpdateBehaviour.PreRelease;
        case mask === "":
        case /^\d+\.X$/i.test(mask):
            return UpdateBehaviour.Minor;
        case /^\d+\.\d+\.X$/i.test(mask):
            return UpdateBehaviour.Patch;
        default:
            return UpdateBehaviour.Invalid;
    }
}
export function getVersionMask(version: string, updateBehaviour: UpdateBehaviour): string {
    const major = semver.major(version);
    const minor = semver.minor(version);
    switch (updateBehaviour) {
        case UpdateBehaviour.Invalid:
        case UpdateBehaviour.Minor:
            return `${major}.X`;
        case UpdateBehaviour.Patch:
            return `${major}.${minor}.X`;
        case UpdateBehaviour.PreRelease:
            return "PreRelease";
        default:
            exhaustiveCheck(updateBehaviour, "Invalid UpdateBehaviour");
    }
}
function getUpdatedVersionMask(latestVersion: string, originalVersion: string | undefined | null, updateBehaviour: UpdateBehaviour, originalUpdateBehaviour: UpdateBehaviour): string {
    if (!originalVersion)
        return getVersionMask(latestVersion, updateBehaviour);
    if (updateBehaviour === originalUpdateBehaviour)
        return getVersionMask(originalVersion, updateBehaviour);
    const isMinorToPatchOrViceVersa = (originalUpdateBehaviour === UpdateBehaviour.Minor && updateBehaviour === UpdateBehaviour.Patch) || (originalUpdateBehaviour === UpdateBehaviour.Patch && updateBehaviour === UpdateBehaviour.Minor);
    if (isMinorToPatchOrViceVersa) {
        return getVersionMask(originalVersion, updateBehaviour);
    }
    else {
        return getVersionMask(latestVersion, updateBehaviour);
    }
}
function OnlyPreReleaseCallout() {
    return <Callout type={"information"} title={"Only pre-release versions of this template are available."}/>;
}
const styles = {
    helperText: css({ fontSize: fontSize.xSmall, color: themeTokens.color.text.secondary, marginBottom: space[12] }),
    templateVersionTitle: css({ display: "inline-flex", alignItems: "center", gap: space[4] }),
};
